import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import type { UserRole } from '@/types/enums'
import type { Profile, UserOrderStats } from '@/types/user'
import { createClient } from '@/api/supabase'
import { digitsOnly } from '@/utils/phone'
import i18n from '@/utils/i18n'

/** Нормализует телефон до E.164 (+7XXXXXXXXXX) перед отправкой в RPC. */
function normalizePhoneE164(phone: string): string {
  return `+${digitsOnly(phone)}`
}

export interface UserListParams {
  page?: number
  pageSize?: number
  role?: UserRole | 'all'
  companyId?: string
  search?: string
  includeInactive?: boolean
}

export interface UserListResult {
  items: Profile[]
  total: number
}

const USER_PHOTO_BUCKET = 'inspection-photos'

export function getUserPhotoUrl(profile: Pick<Profile, 'access_scope'> & { avatar_url?: string | null }): string | null {
  const raw =
    (typeof profile.avatar_url === 'string' && profile.avatar_url.trim()) ||
    (typeof profile.access_scope?.avatar_url === 'string' && profile.access_scope.avatar_url.trim())
  if (!raw) return null
  const value = raw as string
  if (/^https?:\/\//i.test(value)) return value
  if (USE_MOCK) return value
  const supabase = createClient()
  const { data } = supabase.storage.from(USER_PHOTO_BUCKET).getPublicUrl(value)
  return data.publicUrl || null
}

export function mergeUserPhotoIntoScope(
  accessScope: Record<string, unknown> | null | undefined,
  avatarUrl: string | null,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(accessScope ?? {}) }
  if (avatarUrl) {
    next.avatar_url = avatarUrl
  } else {
    delete next.avatar_url
  }
  return next
}

function mockList(p: UserListParams): UserListResult {
  const page = Math.max(1, p.page ?? 1)
  const pageSize = Math.min(100, p.pageSize ?? 25)
  let rows = [...mockState.profiles]
  if (!p.includeInactive) rows = rows.filter((u) => u.is_active)
  if (p.role && p.role !== 'all') {
    rows = rows.filter((u) => u.role === p.role)
  }
  if (p.companyId) {
    rows = rows.filter((u) => u.company_id === p.companyId)
  }
  if (p.search?.trim()) {
    const q = p.search.trim().toLowerCase()
    rows = rows.filter(
      (u) =>
        u.full_name.toLowerCase().includes(q) || u.phone.replace(/\D/g, '').includes(q.replace(/\D/g, '')),
    )
  }
  rows.sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'))
  const total = rows.length
  const start = (page - 1) * pageSize
  return { items: rows.slice(start, start + pageSize), total }
}

export async function listUsers(
  params: UserListParams,
): Promise<UserListResult> {
  if (USE_MOCK) return mockList(params)

  const supabase = createClient()
  const page = Math.max(1, params.page ?? 1)
  const pageSize = Math.min(100, params.pageSize ?? 25)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  let query = supabase.from('profiles').select('*', { count: 'exact' })

  if (!params.includeInactive) {
    query = query.eq('is_active', true)
  }
  if (params.role && params.role !== 'all') {
    query = query.eq('role', params.role)
  }
  if (params.companyId) {
    query = query.eq('company_id', params.companyId)
  }
  if (params.search?.trim()) {
    query = query.ilike('full_name', `%${params.search.trim()}%`)
  }

  const { data, error, count } = await query
    .order('full_name', { ascending: true })
    .range(from, to)

  if (error) throw new Error(error.message)
  return { items: (data ?? []) as Profile[], total: count ?? 0 }
}

export async function getProfile(id: string): Promise<Profile | null> {
  if (USE_MOCK) return mockState.profiles.find((p) => p.id === id) ?? null

  const supabase = createClient()
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw new Error(error.message)
  }
  return data as Profile
}

/** Синхронная версия только для мока (используется в таблицах) */
export function mockUserOrderStats(userId: string): UserOrderStats {
  const orders = mockState.orders.filter(
    (o) => o.manager_id === userId || o.inspector_id === userId,
  )
  const total = orders.length
  const active = orders.filter((o) =>
    [
      'SEARCH', 'ASSIGNED', 'DRIVING', 'ARRIVED', 'INSPECTION', 'APPRAISAL',
      'BUYING', 'DOCUMENT_PREPARATION', 'PAYMENT_TRANSFER', 'TRANSPORTATION',
    ].includes(o.status),
  ).length
  const completed = orders.filter((o) => o.status === 'COMPLETED').length
  const cancelled = orders.filter((o) => o.status === 'CANCELLED').length
  const refused = orders.filter((o) =>
    ['REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'].includes(o.status),
  ).length
  return { total, active, completed, cancelled, refused }
}

export async function userOrderStats(userId: string): Promise<UserOrderStats> {
  if (USE_MOCK) {
    const orders = mockState.orders.filter(
      (o) => o.manager_id === userId || o.inspector_id === userId,
    )
    const total = orders.length
    const active = orders.filter((o) =>
      [
        'SEARCH', 'ASSIGNED', 'DRIVING', 'ARRIVED', 'INSPECTION', 'APPRAISAL',
        'BUYING', 'DOCUMENT_PREPARATION', 'PAYMENT_TRANSFER', 'TRANSPORTATION',
      ].includes(o.status),
    ).length
    const completed = orders.filter((o) => o.status === 'COMPLETED').length
    const cancelled = orders.filter((o) => o.status === 'CANCELLED').length
    const refused = orders.filter((o) =>
      ['REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'].includes(o.status),
    ).length
    return { total, active, completed, cancelled, refused }
  }

  const supabase = createClient()
  const { data, error } = await supabase.rpc('user_order_stats', { p_user_id: userId })
  if (error) throw new Error(error.message)
  // Миграция 20260418000200 возвращает cancelled/refused дополнительно;
  // старый билд RPC не падает — просто поля отсутствуют.
  const stats = data as {
    total?: number
    active?: number
    completed?: number
    cancelled?: number
    refused?: number
  }
  return {
    total: stats.total ?? 0,
    active: stats.active ?? 0,
    completed: stats.completed ?? 0,
    cancelled: stats.cancelled ?? 0,
    refused: stats.refused ?? 0,
  }
}

const ACTIVE_STATUSES = [
  'SEARCH',
  'ASSIGNED',
  'DRIVING',
  'ARRIVED',
  'INSPECTION',
  'APPRAISAL',
  'BUYING',
  'DOCUMENT_PREPARATION',
  'PAYMENT_TRANSFER',
  'TRANSPORTATION',
] as const

/**
 * Bulk-версия статистики заказов для списка пользователей. Один запрос за все
 * видимые строки таблицы — нужен, потому что построчный useQuery на каждой
 * строке раньше ходил в mockState (`mockUserOrderStats`) и в реальном режиме
 * всегда рисовал `0 / 0 / 0` в колонке «Заказы».
 */
export async function listUserOrderStatsBulk(
  userIds: string[],
): Promise<Record<string, UserOrderStats>> {
  const out: Record<string, UserOrderStats> = {}
  if (userIds.length === 0) return out
  const empty = (): UserOrderStats => ({
    total: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
    refused: 0,
  })
  for (const id of userIds) out[id] = empty()

  if (USE_MOCK) {
    for (const id of userIds) {
      const orders = mockState.orders.filter(
        (o) => o.manager_id === id || o.inspector_id === id,
      )
      const s = out[id]
      s.total = orders.length
      s.active = orders.filter((o) =>
        (ACTIVE_STATUSES as readonly string[]).includes(o.status),
      ).length
      s.completed = orders.filter((o) => o.status === 'COMPLETED').length
      s.cancelled = orders.filter((o) => o.status === 'CANCELLED').length
      s.refused = orders.filter((o) =>
        ['REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'].includes(o.status),
      ).length
    }
    return out
  }

  const supabase = createClient()
  // orders.manager_id/inspector_id → auth.users; один OR-запрос по обоим ролям.
  // Подтягиваем только нужные колонки, без pagination-лимита (RLS уже сузит).
  const orFilter = [
    `manager_id.in.(${userIds.join(',')})`,
    `inspector_id.in.(${userIds.join(',')})`,
  ].join(',')
  const { data, error } = await supabase
    .from('orders')
    .select('status, manager_id, inspector_id')
    .or(orFilter)
  if (error) throw new Error(error.message)

  for (const row of (data ?? []) as Array<{
    status: string
    manager_id: string | null
    inspector_id: string | null
  }>) {
    const ids = [row.manager_id, row.inspector_id].filter(
      (v): v is string => !!v && userIds.includes(v),
    )
    for (const id of ids) {
      const s = out[id]
      if (!s) continue
      s.total += 1
      if ((ACTIVE_STATUSES as readonly string[]).includes(row.status)) s.active += 1
      if (row.status === 'COMPLETED') s.completed += 1
      if (row.status === 'CANCELLED') s.cancelled += 1
      if (['REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'].includes(row.status)) {
        s.refused += 1
      }
    }
  }
  return out
}

export interface ClientDebugRow {
  id: string
  user_id: string
  created_at: string
  session_id: string
  platform: string
  app_version: string
  kind: string
  name: string
  outcome: string | null
  message: string
  payload: unknown
}

/**
 * Карточка «Устройство» в UserDetailPage: берём последний client_debug_events
 * по пользователю (через миграцию 20260405140000 у всех can_view_debug_logs=true).
 * Раньше шло только в `mockState.clientDebug` — на реале всегда было «Нет данных».
 */
export async function getClientDebug(
  userId: string,
): Promise<ClientDebugRow | null> {
  if (USE_MOCK) {
    const row = mockState.clientDebug.find((e) => e.user_id === userId)
    if (!row) return null
    return {
      id: `mock-${row.user_id}`,
      user_id: row.user_id,
      created_at: row.created_at,
      session_id: '',
      platform: '',
      app_version: '',
      kind: 'mock',
      name: 'mock',
      outcome: null,
      message: '',
      payload: row.payload,
    }
  }
  const supabase = createClient()
  const { data, error } = await supabase
    .from('client_debug_events')
    .select(
      'id, user_id, created_at, session_id, platform, app_version, kind, name, outcome, message, payload',
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) {
    // RLS / пустая таблица — тихо показываем «нет данных», чтобы не сломать страницу.
    return null
  }
  return (data ?? null) as ClientDebugRow | null
}

/**
 * Черновик пароля для показа в UI (создание пользователя, сброс пароля).
 * Генерируем произносимое слово из чередующихся согласных и гласных
 * (нижний регистр, 10 символов) — легко продиктовать.
 */
export function generateProvisionalPassword(): string {
  const V = 'aeiou'
  const C = 'bcdfghjklmnprstvz'
  let s = ''
  for (let i = 0; i < 10; i++) {
    const pool = i % 2 === 0 ? C : V
    s += pool[Math.floor(Math.random() * pool.length)]
  }
  return s
}

export async function createUser(payload: {
  full_name: string
  phone: string
  email?: string | null
  company_id: string | null
  role: UserRole
  has_pd_access: boolean
}): Promise<{ profile: Profile; password: string }> {
  const password = generateProvisionalPassword()
  if (USE_MOCK) {
    const now = new Date().toISOString()
    const profile: Profile = {
      id: `user-${crypto.randomUUID().slice(0, 8)}`,
      full_name: payload.full_name,
      last_name: null,
      first_name: null,
      middle_name: null,
      phone: payload.phone,
      email: payload.email ?? null,
      company_id: payload.company_id,
      role: payload.role,
      is_active: true,
      status: 'active',
      deleted_at: null,
      has_pd_access: payload.has_pd_access,
      must_change_password: true,
      passport_series: null,
      passport_number: null,
      passport_issued_by: null,
      passport_issued_at: null,
      power_of_attorney_number: null,
      power_of_attorney_expires_at: null,
      registration_address: null,
      passport_main_url: null,
      passport_registration_url: null,
      power_of_attorney_url: null,
      created_at: now,
      updated_at: now,
      last_sign_in_at: null,
    }
    if (mockState.profiles.some((p) => p.phone === payload.phone)) {
      throw new Error(i18n.t('apiErr.users.phoneTaken'))
    }
    mockState.profiles.push(profile)
    console.info('[mock] seed password for', profile.phone, password)
    return { profile, password }
  }

  const supabase = createClient()
  const { data, error } = await supabase.rpc('create_user_with_password', {
    p_phone: normalizePhoneE164(payload.phone),
    p_password: password,
    p_full_name: payload.full_name,
    p_role: payload.role,
    p_company_id: payload.company_id ?? null,
    p_has_pd_access: payload.has_pd_access,
  })

  if (error) throw new Error(error.message)

  const profile = await getProfile(data as string)
  if (!profile) throw new Error(i18n.t('apiErr.users.profileNotFoundAfterCreate'))
  return { profile, password }
}

export async function updateProfile(
  id: string,
  data: Partial<Profile>,
): Promise<Profile> {
  if (USE_MOCK) {
    const i = mockState.profiles.findIndex((p) => p.id === id)
    if (i < 0) throw new Error(i18n.t('apiErr.users.userNotFound'))
    mockState.profiles[i] = {
      ...mockState.profiles[i],
      ...data,
      id,
      updated_at: new Date().toISOString(),
    }
    return mockState.profiles[i]
  }

  const supabase = createClient()
  const { phone, ...rest } = data
  if (typeof phone === 'string') {
    const { error: syncError } = await supabase.rpc('update_user_phone_and_auth_email', {
      p_user_id: id,
      p_phone: normalizePhoneE164(phone),
    })
    if (syncError) throw new Error(syncError.message)
  }

  if (Object.keys(rest).length === 0) {
    const profile = await getProfile(id)
    if (!profile) throw new Error(i18n.t('apiErr.users.userNotFoundAfterPhoneUpdate'))
    return profile
  }

  const { data: updated, error } = await supabase
    .from('profiles')
    .update({ ...rest, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return updated as Profile
}

export async function uploadUserPhoto(
  userId: string,
  file: File,
): Promise<string> {
  if (USE_MOCK) {
    return URL.createObjectURL(file)
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg'
  const safeExt = ext.replace(/[^a-z0-9]/g, '') || 'jpg'
  const path = `profiles/${userId}/avatar-${Date.now()}.${safeExt}`
  const supabase = createClient()
  const { error } = await supabase.storage
    .from(USER_PHOTO_BUCKET)
    .upload(path, file, {
      upsert: true,
      contentType: file.type || 'image/jpeg',
      cacheControl: '3600',
    })
  if (error) throw new Error(error.message)
  const { data } = supabase.storage.from(USER_PHOTO_BUCKET).getPublicUrl(path)
  if (!data.publicUrl) throw new Error(i18n.t('apiErr.users.photoUrlFailed'))
  return data.publicUrl
}

const USER_DOCUMENTS_BUCKET = 'user-documents'

/** Тип документа в bucket `user-documents`. Путь: `{userId}/{kind}.{ext}`. */
export type UserDocumentKind = 'passport-main' | 'passport-registration' | 'power-of-attorney'

/**
 * Загрузка документа профиля в приватный bucket `user-documents`.
 * Возвращает путь объекта (используется как идентификатор для signed URL).
 * Bucket приватный (миграция `20260505130000` репо `stryzh`); для отображения
 * нужен `getUserDocumentSignedUrl`.
 */
export async function uploadUserDocument(
  userId: string,
  kind: UserDocumentKind,
  file: File,
): Promise<string> {
  if (USE_MOCK) {
    return URL.createObjectURL(file)
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg'
  const safeExt = ext.replace(/[^a-z0-9]/g, '') || 'jpg'
  const path = `${userId}/${kind}.${safeExt}`
  const supabase = createClient()
  const { error } = await supabase.storage
    .from(USER_DOCUMENTS_BUCKET)
    .upload(path, file, {
      upsert: true,
      contentType: file.type || 'application/octet-stream',
      cacheControl: '3600',
    })
  if (error) throw new Error(error.message)
  return path
}

/** Возвращает временный signed URL для приватного объекта (1 час). */
export async function getUserDocumentSignedUrl(
  path: string,
  expiresInSec = 3600,
): Promise<string | null> {
  if (USE_MOCK) return path
  if (!path) return null
  const supabase = createClient()
  const { data, error } = await supabase.storage
    .from(USER_DOCUMENTS_BUCKET)
    .createSignedUrl(path, expiresInSec)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}

export async function deactivateUser(id: string): Promise<void> {
  if (USE_MOCK) {
    const open = mockState.orders.some(
      (o) =>
        (o.manager_id === id || o.inspector_id === id) &&
        !['COMPLETED', 'CANCELLED', 'REFUSED_BY_INSPECTOR', 'REFUSED_BY_SELLER', 'REFUSED_BUYOUT'].includes(o.status),
    )
    if (open) throw new Error(i18n.t('apiErr.users.unfinishedOrders'))
    const admins = mockState.profiles.filter((p) => p.role === 'admin' && p.is_active)
    const target = mockState.profiles.find((p) => p.id === id)
    if (target?.role === 'admin' && admins.length === 1) {
      throw new Error(i18n.t('apiErr.users.lastAdmin'))
    }
    await updateProfile(id, { is_active: false, status: 'blocked' })
    return
  }

  const supabase = createClient()
  const { error } = await supabase.rpc('deactivate_user', { p_user_id: id })
  if (error) throw new Error(error.message)
}

/**
 * Снять блокировку и/или soft-delete с пользователя.
 * RPC из миграции `20260505120000_status_sync_rpcs` репо `stryzh`.
 */
export async function activateUser(id: string): Promise<void> {
  if (USE_MOCK) {
    await updateProfile(id, { is_active: true, status: 'active', deleted_at: null })
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('activate_user', { p_user_id: id })
  if (error) throw new Error(error.message)
}

/**
 * Soft-delete пользователя (выставляет `deleted_at = now()`).
 * RPC из миграции `20260505120000_status_sync_rpcs` репо `stryzh`.
 */
export async function softDeleteUser(id: string): Promise<void> {
  if (USE_MOCK) {
    await updateProfile(id, {
      is_active: false,
      deleted_at: new Date().toISOString(),
    })
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('soft_delete_user', { p_user_id: id })
  if (error) throw new Error(error.message)
}

export async function resetPassword(): Promise<string> {
  return generateProvisionalPassword()
}

export interface UserAuthDebug {
  auth_user: {
    id: string
    email: string | null
    phone: string | null
    email_confirmed_at: string | null
    phone_confirmed_at: string | null
    last_sign_in_at: string | null
    banned_until: string | null
    deleted_at: string | null
    created_at: string
    updated_at: string
    is_super_admin: boolean | null
    is_anonymous: boolean
    raw_app_meta_data: Record<string, unknown> | null
  } | null
  identities: Array<{
    id: string
    provider: string
    provider_id: string
    identity_email: string | null
    last_sign_in_at: string | null
    created_at: string
    updated_at: string
  }>
}

/**
 * Технические данные авторизации (auth.users + auth.identities) для диагностики
 * в карточке пользователя. RPC `get_user_auth_debug` доступен только админам.
 */
export async function getUserAuthDebug(userId: string): Promise<UserAuthDebug | null> {
  if (USE_MOCK) {
    const u = mockState.profiles.find((p) => p.id === userId)
    if (!u) return null
    const digits = digitsOnly(u.phone)
    const email = `${digits}@stryzh.app`
    return {
      auth_user: {
        id: u.id,
        email,
        phone: null,
        email_confirmed_at: u.created_at,
        phone_confirmed_at: null,
        last_sign_in_at: u.last_sign_in_at ?? null,
        banned_until: u.is_active ? null : u.updated_at,
        deleted_at: u.deleted_at ?? null,
        created_at: u.created_at,
        updated_at: u.updated_at,
        is_super_admin: false,
        is_anonymous: false,
        raw_app_meta_data: { provider: 'email', providers: ['email'] },
      },
      identities: [
        {
          id: u.id,
          provider: 'email',
          provider_id: email,
          identity_email: email,
          last_sign_in_at: u.last_sign_in_at ?? null,
          created_at: u.created_at,
          updated_at: u.updated_at,
        },
      ],
    }
  }
  const supabase = createClient()
  const { data, error } = await supabase.rpc('get_user_auth_debug', { p_user_id: userId })
  if (error) {
    // Не-админ не должен ронять страницу — просто не показываем секцию.
    return null
  }
  return data as UserAuthDebug
}

/**
 * Установка нового пароля пользователю (только admin; RPC в live).
 * Длина и сложность не ограничиваются на клиенте.
 */
export async function resetPasswordForUser(
  userId: string,
  newPassword: string,
): Promise<void> {
  if (USE_MOCK) {
    await updateProfile(userId, { must_change_password: true })
    console.info('[mock] reset password for', userId, '→', newPassword)
    return
  }
  const supabase = createClient()
  const { error } = await supabase.rpc('reset_user_password', {
    p_user_id: userId,
    p_password: newPassword,
  })
  if (error) throw new Error(error.message)
}
